<template>
  <div
    v-if="isActive"
    id="basemapID"
    class="map-tool-window basemap-panel"
  >
    <div id="basemapIDheader" class="map-tool-window-header">
      <span class="map-tool-window-header-title">Cambiar Mapa Base</span>
      <div class="map-tool-window-header-actions">
        <button class="control-btn" @click.stop="minimized = !minimized">
            <svg  v-if="minimized"
            viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <svg v-else
              viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
        <button class="control-btn" title="Cerrar" @click.stop="close">
              <svg viewBox="0 0 24 24"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>
        </button>
      </div>
    </div>
    <div v-if="!minimized" class="basemap-body">
      <div class="basemap-grid">
        <div
          v-for="item in catalog"
          :key="item.id"
          class="basemap-card"
          :class="{ active: item.id === activeId }"
          @click="select(item)"
        >
          <img :src="item.thumbnail" :alt="item.name" @error="onThumbError" />
          <div class="basemap-name">{{ item.name }}</div>
        </div>
      </div>
      <div class="basemap-offline">
        <div class="basemap-offline-title">Mapa Local (GeoPackage)</div>
        <div class="basemap-offline-actions">
          <button
            v-if="serverGpkgUrl"
            class="offline-btn"
            :disabled="gpkgLoading"
            @click="loadServerGpkg"
          >Servidor</button>
          <label class="offline-btn" :class="{ disabled: gpkgLoading }">
            Archivo local
            <input
              type="file"
              accept=".gpkg"
              style="display: none"
              :disabled="gpkgLoading"
              @change="onGpkgFile"
            />
          </label>
        </div>
        <div v-if="gpkgLoading" class="offline-progress">
          <span>{{ gpkgPhase }}</span>
          <progress v-if="gpkgProgress !== null" :value="gpkgProgress" max="100"></progress>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { vuex } from "@/components/mixin/vuex.js";
import { draggableDiv } from "@/components/mixin/draggableDiv.js";
import { mapService } from "@/services/map/mapService.js";
import { geopackageService, MAX_GPKG_BYTES, OFFLINE_FLAG } from "@/services/map/geopackageService.js";
import {
  baseMapCatalog,
  DEFAULT_BASEMAP_ID,
  PLACEHOLDER_THUMB_URL,
} from "@/components/mapTools/baseMap/baseMapCatalog.js";

export default {
  name: "ChangeBasemap",
  mixins: [vuex, draggableDiv],
  data() {
    return {
      catalog: baseMapCatalog,
      activeId: sessionStorage.getItem(OFFLINE_FLAG) ? "gpkg" : DEFAULT_BASEMAP_ID,
      minimized: false,
      gpkgLoading: false,
      gpkgProgress: null,
      gpkgPhase: "",
    };
  },
  computed: {
    isActive() {
      return this.mapToolValues("basemap").active;
    },
    serverGpkgUrl() {
      return process.env.VUE_APP_OFFLINE_BASEMAP_URL || null;
    },
  },
  methods: {
    select(item) {
      mapService.changeBaseMap(item);
      this.activeId = item.id;
      geopackageService.clearOfflineFlag();
      //this.close();
      //segun observacion SENAPRED 06/2026 ("La herramienta (Capas Temáticas / Mapas Base) no se debe cerrar al escoger un mapa base.")
    },
    async onGpkgFile(e) {
      const file = e.target.files[0];
      e.target.value = "";
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".gpkg")) {
        alert("El archivo debe ser un GeoPackage (.gpkg)");
        return;
      }
      if (file.size > MAX_GPKG_BYTES) {
        alert("El archivo supera el límite de 1.5 GB soportado por el visor");
        return;
      }
      await this.activateGpkg(
        () => geopackageService.readGpkgFile(file, (p) => (this.gpkgProgress = p)),
        "Importando archivo…"
      );
    },
    async loadServerGpkg() {
      await this.activateGpkg(
        () => geopackageService.fetchGpkg(this.serverGpkgUrl, (p) => (this.gpkgProgress = p)),
        "Descargando mapa del servidor…"
      );
    },
    async activateGpkg(getBuffer, phaseLabel) {
      this.gpkgLoading = true;
      this.gpkgProgress = 0;
      this.gpkgPhase = phaseLabel;
      try {
        const buffer = await getBuffer();
        this.gpkgPhase = "Procesando GeoPackage…";
        this.gpkgProgress = null;
        const layer = await geopackageService.buildGpkgLayer(buffer);
        mapService.applyBaseLayerDirect(layer);
        this.activeId = "gpkg";
        await geopackageService.persistGpkg(buffer);
      } catch (err) {
        console.error("[ChangeBasemap] Error cargando GeoPackage:", err);
        alert("Error cargando GeoPackage: " + err.message);
      } finally {
        this.gpkgLoading = false;
        this.gpkgProgress = null;
        this.gpkgPhase = "";
      }
    },
    close() {
      this.selectMapTool("basemap", false);
    },
    onThumbError(e) {
      if (e.target.src !== PLACEHOLDER_THUMB_URL) {
        e.target.src = PLACEHOLDER_THUMB_URL;
      }
    },
  },
  mounted() {
    this.makeDraggable("basemapID");
  },
};
</script>

<style scoped lang="scss">
.basemap-panel {
  position: fixed;
  width: 320px;
  max-height: 70vh;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  z-index: 100000001;
  display: flex;
  flex-direction: column;
  user-select: none;
}

.basemap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  cursor: move;
  font-weight: 600;
  background: #f9f9f9;
  border-radius: 8px 8px 0 0;
}

.basemap-title {
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 4px;
}

.btn-icon {
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 8px;
  color: #333;
  border-radius: 4px;

  &:hover {
    background: #e5e5e5;
  }
}

// Body partido: el grid scrollea, la sección offline queda fija abajo.
// Sin max-height fija porque la altura de .basemap-offline varía (progreso, botón Servidor).
.basemap-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.basemap-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.basemap-card {
  cursor: pointer;
  text-align: center;
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 4px;
  transition: border-color 0.15s, background 0.15s;
  background: #fafafa;

  &:hover {
    border-color: #bbb;
    background: #f0f0f0;
  }

  &.active {
    border-color: var(--color-orange);
    background: var(--color-mist);
  }

  img {
    width: 100%;
    height: 90px;
    object-fit: cover;
    border-radius: 4px;
    display: block;
    background: #ddd;
  }
}

.basemap-name {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.2;
  color: #333;
}

.basemap-offline {
  border-top: 1px solid #e0e0e0;
  padding: 10px 12px;
  flex-shrink: 0;
}
.basemap-offline-title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}
.basemap-offline-actions {
  display: flex;
  gap: 8px;
}
.offline-btn {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  margin: 0;
  opacity: 1;
  outline: none;
  box-shadow: none;
  text-align: center;
  text-decoration: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 0 8px;
  border: 1px solid #bbb;
  border-radius: 4px;
  background: #fafafa;
  color: #333;
  cursor: pointer;
  user-select: none;
  &:hover { background: #f0f0f0; }
  &.disabled, &:disabled { opacity: 0.5; cursor: default; }
}
.offline-progress {
  margin-top: 8px;
  font-size: 11px;
  color: #333;
  progress { width: 100%; }
}
</style>
